import { getAdminUrl } from "@pathwright/utils/src/url/admin"
import { getReleativeRoute } from "./getAuthRoute"
import getNextUrl from "./getNextUrl"

// Redirects to the PW admin SSO URL if derived from the username/email input.
export async function handlePWAdminSSORedirect<T extends string>(input: T) {
  const ssoUrl = getPWAdminSSOUrl(input)
  if (ssoUrl) {
    window.location.href = ssoUrl
    await new Promise(() => {}) // Wait indefinitely for redirect.
  }
}

// Handles inserting a PW admin SSO option in local storage for convenience, like
// listing login buttons in the auth flow per record.
function storePWAdminSSOOption(targetUser: string) {
  // Get the locally stored sso options (and array of strings stored at the key "admin::")
  const options = localStorage.getItem("admin::") || "[]"
  const parsedOptions = JSON.parse(options)

  // Only proceed if the input is not already stored.
  if (!parsedOptions.includes(targetUser)) {
    parsedOptions.push(targetUser)
    // Note that sorting will always ensure the empty string is first (which is the option
    // to sign in as yourself).
    localStorage.setItem("admin::", JSON.stringify(parsedOptions.sort()))
  }
}

// Handles removing a PW admin SSO option in local storage.
export function removePWAdminSSOOption(targetUser: string) {
  // Get the locally stored sso options (and array of strings stored at the key "admin::")
  const options = localStorage.getItem("admin::") || "[]"
  const parsedOptions = JSON.parse(options)

  // Only proceed if the input is already stored.
  if (parsedOptions.includes(targetUser)) {
    parsedOptions.splice(parsedOptions.indexOf(targetUser), 1)
    localStorage.setItem("admin::", JSON.stringify(parsedOptions))
  }
}

// Retrieves the stored PW admin SSO options.
export function getPWAdminSSOOptions(): string[] {
  const options = localStorage.getItem("admin::")
  return options ? JSON.parse(options) : []
}

// The acting user is the word "admin" followed by two colons *optionally*
// followed by the target username/email.
const mixedInputRe = /^(?<actingUser>admin)::(?<targetUser>[^:]*)$/

/**
 * Generates a Pathwright Admin SSO URL for proxy login as another user.
 *
 * The input string should be in the format `"admin::<targetUser?>"`, where `<targetUser>` is the username or email to proxy into.
 * The resulting URL includes the following query parameters:
 *
 * - `school_id`: The current school/space ID, retrieved from localStorage as `"PW_SPACE"`.
 * - `proxy_into`: (Optional) The target user to proxy into, extracted from the input string.
 * - `redirect_url`: The absolute URL to redirect to after SSO, determined from the next URL or current location.
 *
 * @param input - A string in the format `"admin::<targetUser>"` to specify the acting and target user.
 * @returns The constructed Pathwright Admin SSO URL as a string, or `void` if the input does not match the expected format.
 */

export type PWAdminSSOString = `admin::${string}`

// Safely type the return based on the input
type GetPWAdminSSOUrlReturn<T extends string> = T extends PWAdminSSOString
  ? string
  : void

function getPWAdminSSOUrl<T extends string>(
  input: T,
  options?: { store?: boolean; url?: string }
): GetPWAdminSSOUrlReturn<T> {
  const match = input.match(mixedInputRe)

  if (match) {
    const { targetUser } = match.groups!

    // Default to always storing the sso option unless explicitly disabled.
    if (options?.store !== false) {
      // Store the sso option based on the target user (which may be an empty string).
      storePWAdminSSOOption(targetUser)
    }

    const spaceId = localStorage.getItem("PW_SPACE")

    // Redirect to the pw admin SSO URL if it exists, first setting the ?next url.
    const url = getAdminUrl("sso/authorize/")

    // Scope to current school.
    url.searchParams.set("school_id", `${spaceId}`)

    // If user is proxying set the proxy_into param.
    if (targetUser) {
      url.searchParams.set("proxy_into", targetUser)
    }

    // Get the ?next url if set, which will possibly be a relative path (in which case
    // we must ensure it is absolute).
    const nextUrl = options?.url || getNextUrl() || window.location.href
    // Check if the nextUrl is relative or absolute.
    const isNextUrlRelative = getReleativeRoute(nextUrl)[0] === nextUrl
    // Ensure the nextUrl is absolute.
    const absNextUrl = isNextUrlRelative
      ? `${window.location.origin}${nextUrl}`
      : nextUrl
    // Set the redirect_url to the absolute nextUrl.
    url.searchParams.set("redirect_url", absNextUrl)

    return url.href as GetPWAdminSSOUrlReturn<T>
  } else {
    return undefined as GetPWAdminSSOUrlReturn<T>
  }
}

// @ts-expect-error: intentionally not defining this fn on the window as we never
// wish to use it in our codebase in that manner, only wanting ot expose it to our
// bookmarklet scripts.
window.getPWAdminSSOUrl = getPWAdminSSOUrl

export default getPWAdminSSOUrl
